import { useMemo, useState } from 'react'

import { Box, Grid, Tag, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Button, F0Dialog, F0Select } from '@factorialco/f0-react'
import { EntitySelect, EntitySelectEntity, Input } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete } from '@factorialco/f0-react/icons/app'
import {
  AtsContractTypesEnum,
  AtsCreateOfferLetterTemplateDocument,
  AtsOfferLetterTemplatesGetDataDocument,
  AtsSettingsOfferLetterTemplatesDocument,
} from 'generated/resources/graphql'
import { isString } from 'lodash'
import groupBy from 'lodash/groupBy'

import DocumentType from 'modules/core/components/DocumentType'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'

interface FormValues {
  companyId: number
  name: string
  legalEntityId?: number
  contractType?: AtsContractTypesEnum
  tags?: string[]
  file: File[]
}

interface Props {
  onClose: () => void
  onSuccess: (id: number) => void
}

export const CreateOfferLetterTemplateModal = ({ onClose, onSuccess }: Props) => {
  const { company, employee } = useCurrentSession()
  const [tagValue, setTagValue] = useState('')
  const [tags, setTags] = useState<string[]>([])

  const { data: offerLetterTemplatesData, loading } = useQuery(
    AtsOfferLetterTemplatesGetDataDocument
  )

  const legalEntities = offerLetterTemplatesData?.companies.legalEntities ?? []
  const contractTypes = offerLetterTemplatesData?.ats.contractTypesConnection.nodes || []

  const [createOfferLetterTemplate, { loading: creatingOfferLetterTemplate }] = useMutation(
    AtsCreateOfferLetterTemplateDocument,
    {
      extractorFn: (data) => data.atsMutations.createAtsOfferLetterTemplate,
      refetchQueries: [AtsSettingsOfferLetterTemplatesDocument],
    }
  )

  const form = useForm<FormValues>({
    defaultValues: {
      companyId: company?.id,
      name: '',
      file: [],
      legalEntityId: undefined,
      contractType: undefined,
      tags: [],
    },
    validate(values) {
      const errors: FormErrors<FormValues> = {}
      if (!values.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      if (!values.file.length) {
        errors.file = i18n.t('common.validations.empty')
      }

      return errors
    },
    validateOnChange: false,
    validateOnBlur: false,
    async onSubmit(values, { setErrors }) {
      const result = await createOfferLetterTemplate({
        variables: {
          companyId: company?.id,
          employeeId: employee?.id,
          contractTypes: values.contractType ? [values.contractType] : undefined,
          file: values.file[0],
          name: values.name,
          legalEntityId: values.legalEntityId,
          tags: tags.length > 0 ? tags : undefined,
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success && !!handled.data?.offerLetterTemplate?.id) {
        app.setFlash({
          message: i18n.t('common.saved_successfully'),
          type: 'correct',
        })

        onSuccess(handled.data.offerLetterTemplate.id)
      }

      return handled
    },
  })

  const formattedContractTypes = contractTypes.map(({ id, label, country }) => ({
    label,
    country,
    value: id,
  }))

  const contractTypesEntities: EntitySelectEntity[] = useMemo(() => {
    const groupedFormattedContractTypes = groupBy(
      formattedContractTypes,
      (item) => item.country ?? 'all'
    )
    const entities: EntitySelectEntity[] = Object.entries(groupedFormattedContractTypes).map(
      ([countryKey, items]) => ({
        id: `group_${countryKey}`,
        name: countryKey,
        expanded: true,
        subItems: items
          .filter((i) => i.label)
          .sort((x, y) => x.label.localeCompare(y.label))
          .map(({ value, label, country: countryCode }) => ({
            subId: value,
            subName: label,
            subSearchKeys: [label, value, countryCode ?? ''],
          })),
      })
    )
    return entities
  }, [formattedContractTypes])

  const selectedEntities: EntitySelectEntity[] = useMemo(() => {
    const selectedId = form.values.contractType
    if (!selectedId) return []

    for (const group of contractTypesEntities) {
      const sub = group.subItems?.find((s) => s.subId === selectedId)
      if (sub) return [{ ...group, subItems: [sub] }]
    }

    return []
  }, [contractTypesEntities, form.values.contractType])

  const addTag = () => {
    if (tagValue && !tags.includes(tagValue)) {
      setTags([...tags, tagValue])
    } else {
      app.setFlash({
        message: i18n.t(
          'ats.settings.templates.offer_letter_templates.create_modal.tag_already_added'
        ),
        type: 'wrong',
      })
    }
    setTagValue('')
  }
  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag))
  }

  const onDropFile = (files: File[]) => {
    form.setFieldValue('file', form.values.file.concat(files))
  }

  const onRemoveFile = (indexToRemove: number) => {
    form.setFieldValue(
      'file',
      form.values.file?.filter((_file, index) => index !== indexToRemove)
    )
  }

  const downloadOfferLetterTemplate = () => {
    const url = buildUrl('/ats/files/download?filename=offer_letter_template.docx')

    window.open(url, '_blank')
  }

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      width="lg"
      title={i18n.t('ats.settings.templates.offer_letter_templates.create_modal.title')}
      primaryAction={{
        label: i18n.t('common.create'),
        onClick: () => form.handleSubmit(),
        loading: creatingOfferLetterTemplate,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s16">
        <Box width="full" gap="s16">
          <Input
            label={i18n.t(
              'ats.settings.templates.offer_letter_templates.create_modal.name_input_label'
            )}
            name="name"
            placeholder={i18n.t(
              'ats.settings.templates.offer_letter_templates.create_modal.name_input_placeholder'
            )}
            size="md"
            onChange={(value) => form.setFieldValue('name', value)}
            value={form.values.name}
            error={form.errors.name}
          />
          <Grid variant="fixed">
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <F0Select
                label={i18n.t(
                  'ats.settings.templates.offer_letter_templates.create_modal.legal_entity_input_label'
                )}
                placeholder={i18n.t('common.select')}
                name="legalEntityId"
                value={form.values.legalEntityId?.toString()}
                onChange={(value: string) => form.setFieldValue('legalEntityId', Number(value))}
                options={legalEntities.map((entity) => ({
                  label: `${entity.legalName} - ${entity.currency}`,
                  value: entity.id.toString(),
                }))}
                size="md"
                showSearchBox
              />
            </Box>
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <EntitySelect
                label={i18n.t(
                  'ats.settings.templates.offer_letter_templates.create_modal.contract_type_input_label'
                )}
                notFoundTitle={i18n.t(
                  'ats.settings.templates.offer_letter_templates.create_modal.contract_type_input_not_found'
                )}
                notFoundSubtitle={''}
                placeholder={i18n.t('common.select')}
                onSelect={(entity) => {
                  if (!entity?.subItems?.[0]) {
                    return
                  }
                  const id = entity.subItems[0].subId
                  if (id) {
                    form.setFieldValue('contractType', id)
                  }
                }}
                entities={contractTypesEntities}
                groups={[]}
                selectedGroup="all"
                onItemExpandedChange={() => false}
                onGroupChange={() => false}
                selectedEntities={selectedEntities}
                selectedItemsCopy={i18n.t('common.selected')}
                hiddenAvatar
                singleSelector
                size="md"
                loading={loading}
              />
            </Box>
          </Grid>
          <Box
            gap="s16"
            justifyContent="spaceBetween"
            flexDirection="row"
            flexWrap="noWrap"
            alignItems="flexEnd"
            width="full"
          >
            <Box flexGrow>
              <Input
                label={i18n.t(
                  'ats.settings.templates.offer_letter_templates.create_modal.tags_input_label'
                )}
                name="tags"
                placeholder={i18n.t(
                  'ats.settings.templates.offer_letter_templates.create_modal.tags_input_placeholder'
                )}
                size="md"
                onPressEnter={() => tagValue && addTag()}
                onChange={(value) => setTagValue(value)}
                value={tagValue}
              />
            </Box>
            <F0Button
              onClick={() => addTag()}
              label={i18n.t('common.add')}
              variant="outline"
              icon={Add}
              disabled={!tagValue}
              size="lg"
            />
          </Box>
          <Box flexDirection="row" gap="s12" alignItems="center" flexWrap="wrap">
            {tags.map((tag, index) => (
              <Tag key={index} rightItem="close" type="viridian" onClose={() => removeTag(tag)}>
                {tag}
              </Tag>
            ))}
          </Box>
        </Box>

        {isString(form.errors.file) && (
          <Text.Normal color="radicalRed1000" capitalize size="200">
            {form.errors.file}
          </Text.Normal>
        )}
        {!!form.values.file[0] ? (
          <Box
            flexDirection="row"
            gap="s2"
            paddingX="s16"
            paddingY="s12"
            justifyContent="spaceBetween"
            width="full"
            alignItems="center"
            borderRadius={{ all: 'abs016' }}
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            <Box alignItems="center" flexGrow flexDirection="row" gap="s16" maxHeight="s32">
              <DocumentType mimetype={form.values.file[0].type} />
              <Box overflowX="hidden" maxWidth="s300">
                <Text.Normal size="200" weight="medium" color="textPrimary">
                  {form.values.file[0].name}
                </Text.Normal>
              </Box>
            </Box>

            <F0Button
              onClick={() => onRemoveFile(0)}
              label={i18n.t('common.delete')}
              icon={Delete}
              variant="outline"
              rounded=""
              size="sm"
              hideLabel
            />
          </Box>
        ) : (
          <DropzoneBox
            onDrop={onDropFile}
            iconName="file-upload"
            message={i18n.t('documents.drop_here')}
            accept={acceptedContentTypes([
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'application/docx',
            ])}
            multiple={false}
          />
        )}

        <F0Alert
          title={i18n.t(
            'ats.settings.templates.offer_letter_templates.create_modal.download_template_for_guidance'
          )}
          description={i18n.t(
            'ats.settings.templates.offer_letter_templates.create_modal.download_template_for_guidance_description'
          )}
          variant="info"
          action={{
            onClick: () => downloadOfferLetterTemplate(),
            label: i18n.t('common.download'),
          }}
        />
      </Box>
    </F0Dialog>
  )
}
